// context
import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';

import { useLayoutUpdate } from '../../../../context/LayoutProvider/LayoutProvider';
import { useLanguage } from '../../../../context/LanguageProvider/LanguageProvider';
import { useAuth } from '../../../../context/AuthProvider/AuthProvider';

// components
import NioButton from '../../../NioButton/NioButton';

function FlagSvg({ locale }) {
  if (locale === 'fr') {
    return (
      <svg width="16" height="12" viewBox="0 0 16 12" style={{ borderRadius: 2, flexShrink: 0 }} aria-hidden="true">
        <rect x="0" y="0" width="5.33" height="12" fill="#0055A4" />
        <rect x="5.33" y="0" width="5.33" height="12" fill="#FFFFFF" />
        <rect x="10.66" y="0" width="5.34" height="12" fill="#EF4135" />
      </svg>
    );
  }
  return (
    <svg width="16" height="12" viewBox="0 0 16 12" style={{ borderRadius: 2, flexShrink: 0 }} aria-hidden="true">
      <rect width="16" height="12" fill="#012169" />
      <path d="M0 0 L16 12 M16 0 L0 12" stroke="#FFFFFF" strokeWidth="2.4" />
      <path d="M0 0 L16 12 M16 0 L0 12" stroke="#C8102E" strokeWidth="1.6" />
      <path d="M8 0 V12 M0 6 H16" stroke="#FFFFFF" strokeWidth="4" />
      <path d="M8 0 V12 M0 6 H16" stroke="#C8102E" strokeWidth="2.4" />
    </svg>
  );
}

const LangDropdownToggle = forwardRef(({ langBtnClass, locale, onClick, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    className={classNames('btn', langBtnClass)}
    onClick={onClick}
    aria-label="Langue / Language"
    aria-haspopup="true"
    {...rest}
  >
    <FlagSvg locale={locale} />
    <span className="ms-1 fw-semibold text-uppercase" style={{ fontSize: 11, lineHeight: 1 }}>{locale}</span>
  </button>
));

LangDropdownToggle.displayName = 'LangDropdownToggle';

export default function HeaderAction({
  buttonContent,
  className,
  nioBtnClasses,
  nioToggleClasses,
  langBtnClass,
}) {
  const layoutUpdate = useLayoutUpdate();
  const navigate = useNavigate();
  const { locale, setLocale, t, tNav } = useLanguage();
  const { isAuthenticated, isAdmin, logout } = useAuth();

  const loginLabel = tNav(buttonContent.label || 'Login');
  const fallbackLangClass = 'btn-outline-gray-700 rounded-1 p-2 d-inline-flex align-items-center justify-content-center';

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <div className={className}>
      <ul className="nk-btn-group sm justify-content-center align-items-center">
        <li className="pe-1">
          <Dropdown align="end">
            <Dropdown.Toggle as={LangDropdownToggle} locale={locale} langBtnClass={langBtnClass || fallbackLangClass} id="jassp-lang-dropdown" />
            <Dropdown.Menu>
              <Dropdown.Header className="small text-muted">{t('nav.langMenuTitle')}</Dropdown.Header>
              <Dropdown.Item active={locale === 'fr'} onClick={() => setLocale('fr')}>
                {t('nav.langFr')}
              </Dropdown.Item>
              <Dropdown.Item active={locale === 'en'} onClick={() => setLocale('en')}>
                {t('nav.langEn')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </li>
        {isAuthenticated ? (
          <>
            {isAdmin ? (
              <li className="d-none d-md-block pe-2">
                <NioButton
                  as="link"
                  to="/admin"
                  icon="dashboard"
                  label={t('nav.adminDashboard')}
                  className={nioBtnClasses}
                />
              </li>
            ) : null}
            <li className="d-none d-md-block">
              <NioButton
                icon="signout"
                label={t('nav.logout')}
                className="btn btn-outline-secondary px-3"
                onClick={handleLogout}
              />
            </li>
          </>
        ) : (
          <li className="d-none d-md-block">
            <NioButton
              as="link"
              icon={buttonContent.icon || 'bag'}
              to={buttonContent.redirect || '/auth/login'}
              label={loginLabel}
              className={nioBtnClasses}
            />
          </li>
        )}
        <li className="nk-navbar-toggle">
          <NioButton
            icon="menu"
            className={nioToggleClasses}
            onClick={layoutUpdate.headerMobile}
          />
        </li>
      </ul>
    </div>
  );
}
